/**
 * DropdownPortal - Renders dropdown content via React Portal to escape overflow:hidden containers
 * Smart viewport-aware positioning: flips direction when near edges
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';

export const DropdownPortal = ({
  triggerRef,
  isOpen,
  onClose,
  children,
  align = 'left',   // 'left' | 'center' | 'right'
  side = 'bottom',   // 'bottom' | 'top'
  sideOffset = 4,
  className = '',
  width,             // fixed width or 'trigger' to match trigger width
}) => {
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const contentRef = useRef(null);

  const updatePosition = useCallback(() => {
    if (!triggerRef?.current || !isOpen) return;
    
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const contentEl = contentRef.current;
    const contentHeight = contentEl?.offsetHeight || 200;
    const contentWidth = contentEl?.offsetWidth || (width === 'trigger' ? triggerRect.width : 200);
    
    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;
    const pad = 8;
    
    // Vertical: prefer requested side, flip if not enough space
    let top;
    const spaceBelow = viewportH - triggerRect.bottom - sideOffset;
    const spaceAbove = triggerRect.top - sideOffset;
    
    if (side === 'bottom') {
      top = spaceBelow >= contentHeight || spaceBelow >= spaceAbove
        ? triggerRect.bottom + sideOffset
        : triggerRect.top - contentHeight - sideOffset;
    } else {
      top = spaceAbove >= contentHeight || spaceAbove >= spaceBelow
        ? triggerRect.top - contentHeight - sideOffset
        : triggerRect.bottom + sideOffset;
    }
    
    // Horizontal alignment
    let left;
    if (align === 'right') {
      left = triggerRect.right - contentWidth;
    } else if (align === 'center') {
      left = triggerRect.left + (triggerRect.width / 2) - (contentWidth / 2);
    } else {
      left = triggerRect.left;
    }
    
    // Clamp to viewport
    left = Math.max(pad, Math.min(left, viewportW - contentWidth - pad));
    top = Math.max(pad, Math.min(top, viewportH - contentHeight - pad));
    
    setPosition({ top, left });
  }, [triggerRef, isOpen, align, side, sideOffset, width]);

  useEffect(() => {
    if (!isOpen) return;
    updatePosition();
    // Reposition on scroll/resize
    const handleUpdate = () => requestAnimationFrame(updatePosition);
    window.addEventListener('scroll', handleUpdate, true);
    window.addEventListener('resize', handleUpdate);
    return () => {
      window.removeEventListener('scroll', handleUpdate, true);
      window.removeEventListener('resize', handleUpdate);
    };
  }, [isOpen, updatePosition]);

  // Recompute once content renders (for accurate height)
  useEffect(() => {
    if (isOpen && contentRef.current) {
      requestAnimationFrame(updatePosition);
    }
  }, [isOpen, updatePosition, children]);

  if (!isOpen) return null;

  const widthStyle = width === 'trigger' && triggerRef?.current
    ? { width: triggerRef.current.getBoundingClientRect().width }
    : width ? { width } : {};

  return createPortal(
    <>
      {/* Invisible backdrop to close on outside click */}
      <div
        className="fixed inset-0 z-[9998]"
        onClick={onClose}
        data-testid="dropdown-portal-backdrop"
      />
      {/* Dropdown content */}
      <div
        ref={contentRef}
        className={`fixed z-[9999] ${className}`}
        style={{
          top: position.top,
          left: position.left,
          ...widthStyle,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </>,
    document.body
  );
};

/**
 * Hook to create a trigger ref and toggle state for DropdownPortal
 */
export const useDropdownPortal = () => {
  const triggerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const toggle = useCallback(() => setIsOpen(p => !p), []);
  const close = useCallback(() => setIsOpen(false), []);
  const open = useCallback(() => setIsOpen(true), []);
  return { triggerRef, isOpen, setIsOpen, toggle, close, open };
};

export default DropdownPortal;
